import * as api from 'api/endpoints/brandUsers/brandUsers.api';
import * as BrandUsersTypes from './brandUsers.types';
import { brandUsersError } from 'utils/errorCodes';
import { notificationErr } from 'views/components/UI/notification';
import {
  messageError,
  messageSuccess,
  messageWarning,
} from 'views/components/UI/message';
import { IBrandUser, IcreateUpdateBrandUserProps } from 'interfaces/brandUsers.interface';
import { superAdminRole } from 'utils/constants';
import {
  IcreateBrandUserAPIBody,
  IupdateBrandUserAPIBody,
  IUserPointOfSale,
} from 'api/endpoints/brandUsers/brandUsers.response.interface';
import { ITranslate } from 'interfaces/general.interface';
import { addNotificationsTypeToUserAPI } from 'api/endpoints/notifications/notifications.api';
import { TbrandUsersDispatchReducer } from './brandUsers.reducer';

export async function getBrandUsersAction({
  dispatch,
  clientId,
  userRole,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TbrandUsersDispatchReducer>;
  clientId: string;
  userRole: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: BrandUsersTypes.SET_IS_LOADING_USERS, payload: true });
  try {
    const brandusersFetch = await api.getBrandUsersAPI({ clientId, token });
    if (brandusersFetch.response.status === 200) {
      const orderedBrandUsers: IBrandUser[] = [];
      for (const item of brandusersFetch.data) {
        const userProfile = await api.getBrandUserProfileAPI({ userId: item.id, token });
        const userData = userProfile.data;
        if (userProfile.response.status === 200) {
          const obj: IBrandUser = {
            ...userData,
            ...item,
            key: item.id,
            client_id: clientId,
            user_point_of_sales: (userData.user_point_of_sales as IUserPointOfSale[]).map(
              (point) => point.point_of_sale_id
            ),
          };
          orderedBrandUsers.push(obj);
        }
      }
      dispatch({
        type: BrandUsersTypes.GET_BRAND_USERS,
        payload:
          userRole !== superAdminRole
            ? orderedBrandUsers.filter(
                (item) => item.username.split('@')[1] !== 'hub4retail.com'
              )
            : orderedBrandUsers,
        // payload: orderedBrandUsers,
      });
      return true;
    }
    return false;
  } catch (err) {
    messageError(translate('actions_brand-users_get-brand-users_error'));
    notificationErr(brandUsersError.get_brand_users_001, translate);
    return false;
  } finally {
    dispatch({ type: BrandUsersTypes.SET_IS_LOADING_USERS, payload: false });
  }
}

export function selectBrandUserAction({
  dispatch,
  user,
  translate,
}: {
  dispatch: React.Dispatch<TbrandUsersDispatchReducer>;
  user: IBrandUser | null;
  translate: ITranslate;
}) {
  return dispatch({
    type: BrandUsersTypes.SELECT_BRAND_USER,
    payload: user,
  });
}

export async function createBrandUserAction({
  dispatch,
  clientId,
  newBrandUser,
  token,
  translate,
  addUserClient,
}: {
  dispatch: React.Dispatch<TbrandUsersDispatchReducer>;
  clientId: string;
  newBrandUser: IcreateUpdateBrandUserProps;
  token: string;
  translate: ITranslate;
  addUserClient: (clientId: string, user: IBrandUser) => void;
}): Promise<boolean> {
  dispatch({ type: BrandUsersTypes.SET_IS_LOADING_ACTION, payload: true });
  try {
    const body: IcreateBrandUserAPIBody = {
      user_data: {
        email: newBrandUser.email,
        name: newBrandUser.name,
        nickname: newBrandUser.surname,
        verify_email: true,
        email_verified: true,
        blocked: false,
      },
      user_metadata: {
        client_id: clientId,
        telephone_sufix: '',
        telephone_prefix: '',
        language: newBrandUser.i18n_lang_id,
        sector: newBrandUser.sector,
      },
      role: newBrandUser.role,
    };
    const createFetch = await api.createBrandUserAPI({ body, token });
    if (createFetch.response.status === 409) {
      return messageWarning(translate('actions_brand-users_create-brand-user_exists'));
    }
    if (createFetch.response.status === 201 || createFetch.response.status === 200) {
      const userCreated: IBrandUser = {
        ...createFetch.data.database.db_user,
        key: createFetch.data.database.db_user.id,
        client_id: clientId,
        user_components: [],
        tenant_license_active_users: [],
        user_tenants: [],
        user_point_of_sales: [],
        user_clients: [],
        user_notifications: [],
        i18n_lang: {
          id: '',
          code: '',
          name: '',
        },
      };
      addUserClient(clientId, userCreated);
      dispatch({
        type: BrandUsersTypes.CREATE_BRAND_USER,
        payload: userCreated,
      });
      messageSuccess(translate('actions_brand-users_create-brand-user_success'));
      return true;
    } else {
      messageError(translate('actions_brand-users_create-brand-user_error'));
      notificationErr(brandUsersError.create_brand_user_002, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('actions_brand-users_create-brand-user_error'));
    notificationErr(brandUsersError.create_brand_user_001, translate);
    return false;
  } finally {
    dispatch({ type: BrandUsersTypes.SET_IS_LOADING_ACTION, payload: false });
  }
}

export async function updateBrandUserAction({
  dispatch,
  user,
  userId,
  updateRole,
  prevRole,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TbrandUsersDispatchReducer>;
  user: IcreateUpdateBrandUserProps;
  userId: string;
  updateRole: boolean;
  prevRole: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: BrandUsersTypes.SET_IS_LOADING_ACTION, payload: true });
  if (updateRole) {
    try {
      // Update role
      const updateRoleFetch = await api.updateRoleAPI({
        user_id: userId,
        role: user.role,
        prev_role: prevRole,
        token,
      });
      if (updateRoleFetch.response.status !== 200) {
        messageError(translate('actions_brand-users_update-brand-user_rol-error'));
        notificationErr(brandUsersError.update_brand_user_003, translate);
      }
    } catch (err) {
      messageError(translate('actions_brand-users_update-brand-user_rol-error'));
      notificationErr(brandUsersError.update_brand_user_004, translate);
    }
  }
  try {
    const body: IupdateBrandUserAPIBody = {
      filter: { id: userId },
      update: {
        name: user.name,
        surname: user.surname,
        sector: user.sector,
        i18n_lang_id: user.i18n_lang_id,
      },
      point_of_sale: user.pointsOfSale,
    };
    const updateFetch = await api.updateBrandUserAPI({ body, token });
    if (updateFetch.response.status === 200) {
      try {
        const addNotificationsTypes = await addNotificationsTypeToUserAPI({
          userId,
          notificationsTypesIds: user.notifications,
          token,
        });
        if (
          addNotificationsTypes.response.status === 200 ||
          addNotificationsTypes.response.status === 201
        ) {
          dispatch({
            type: BrandUsersTypes.UPDATE_BRAND_USER,
            payload: {
              key: userId,
              user,
              notifications: Array.isArray(addNotificationsTypes.data)
                ? addNotificationsTypes.data
                : [],
            },
          });
          messageSuccess(translate('actions_brand-users_update-brand-user_success'));
          return true;
        }
        messageError(
          translate('actions_brand-users_update-brand-user_notifications-error')
        );
        notificationErr(brandUsersError.update_brand_user_006, translate);
        return false;
      } catch (error) {
        messageError(translate('actions_brand-users_update-brand-user_error'));
        notificationErr(brandUsersError.update_brand_user_005, translate);
        return false;
      }
    } else {
      messageError(translate('actions_brand-users_update-brand-user_error'));
      notificationErr(brandUsersError.update_brand_user_002, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('actions_brand-users_update-brand-user_error'));
    notificationErr(brandUsersError.update_brand_user_001, translate);
    return false;
  } finally {
    dispatch({ type: BrandUsersTypes.SET_IS_LOADING_ACTION, payload: false });
  }
}

export async function deleteBrandUserAction({
  dispatch,
  userId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TbrandUsersDispatchReducer>;
  userId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const updateFetch = await api.deleteBrandUserAPI({ userId, token });
    if (updateFetch.response.status === 200) {
      dispatch({
        type: BrandUsersTypes.DELETE_BRAND_USER,
        payload: userId,
      });
      messageSuccess(translate('actions_brand-users_delete-brand-user_success'));
      return true;
    } else {
      messageError(translate('actions_brand-users_delete-brand-user_error'));
      notificationErr(brandUsersError.update_brand_user_002, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('actions_brand-users_delete-brand-user_error'));
    notificationErr(brandUsersError.update_brand_user_001, translate);
    return false;
  }
}
